import { useMemo } from "react";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { buildMonthlyBuckets } from "./financials-utils";
import type { Transaction } from "@/types/portfolio.types";

const TODAY_ISO = "2026-03-09";

type Props = {
  transactions: Transaction[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload }: { active?: boolean; payload?: any[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ReturnType<typeof buildMonthlyBuckets>[number] | undefined;
  if (!data) return null;

  return (
    <div className="rounded-lg border border-border bg-background/95 backdrop-blur-sm p-2.5 shadow-lg text-xs space-y-1">
      <p className="font-medium text-foreground">
        {data.label} {data.month.slice(0, 4)}
      </p>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-brand-green" />
        <span className="text-muted-foreground">Inflow:</span>
        <span className="text-foreground font-medium tabular-nums">${data.inflow.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-brand-amber" />
        <span className="text-muted-foreground">Outflow:</span>
        <span className="text-foreground font-medium tabular-nums">${data.outflow.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-brand-blue" />
        <span className="text-muted-foreground">Net:</span>
        <span className="text-foreground font-medium tabular-nums">${data.net.toLocaleString()}</span>
      </div>
      <div className="border-t border-border pt-1 mt-1 flex items-center gap-2">
        <span className="text-muted-foreground">Balance:</span>
        <span className="text-foreground font-semibold tabular-nums">${data.cumulative.toLocaleString()}</span>
      </div>
    </div>
  );
}

function CustomLegend() {
  return (
    <div className="flex items-center justify-center gap-4 mt-2 text-xs text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-brand-green" />
        Inflow
      </div>
      <div className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-brand-amber" />
        Outflow
      </div>
      <div className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-sm bg-brand-blue" />
        Balance
      </div>
    </div>
  );
}

export function CashFlowChart({ transactions }: Props) {
  const data = useMemo(() => buildMonthlyBuckets(transactions, TODAY_ISO), [transactions]);

  const currentMonthLabel = "Mar";
  const todayIdx = data.findIndex((d) => d.label === currentMonthLabel);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Cash Flow</CardTitle>
      </CardHeader>
      <CardPanel>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="financials-balanceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-brand-blue)" stopOpacity={0.15} />
                <stop offset="100%" stopColor="var(--color-brand-blue)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="financials-projectedGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-brand-blue)" stopOpacity={0.06} />
                <stop offset="100%" stopColor="var(--color-brand-blue)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="financials-inflowGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gradient-green-dark-from)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--gradient-green-dark-to)" stopOpacity={0.7} />
              </linearGradient>
              <linearGradient id="financials-outflowGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gradient-amber-red-from)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--gradient-amber-red-to)" stopOpacity={0.7} />
              </linearGradient>
              <linearGradient id="financials-balanceLineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gradient-blue-purple-from)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--gradient-blue-purple-to)" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.08} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "#71717a" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#71717a" }}
              tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
              width={48}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            {todayIdx >= 0 && (
              <ReferenceLine
                x={data[todayIdx].label}
                stroke="rgb(113, 113, 122)"
                strokeDasharray="3 3"
                strokeOpacity={0.4}
                label={{ value: "Today", position: "insideTopRight", fontSize: 10, fill: "#71717a" }}
              />
            )}
            <Bar dataKey="inflow" fill="url(#financials-inflowGrad)" radius={[4, 4, 2, 2]} barSize={24} />
            <Bar dataKey="outflow" fill="url(#financials-outflowGrad)" radius={[4, 4, 2, 2]} barSize={24} />
            <Area
              dataKey="cumulative"
              type="monotone"
              stroke="url(#financials-balanceLineGrad)"
              strokeWidth={2}
              fill="url(#financials-balanceGrad)"
              dot={false}
              activeDot={{ r: 4, fill: "rgb(59, 130, 246)" }}
            />
            <Legend content={<CustomLegend />} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      </CardPanel>
    </Card>
  );
}
